import { useCallback, useEffect, useRef, useState } from 'react';
import { GoogleMap, InfoWindowF, MarkerF, useJsApiLoader } from '@react-google-maps/api';
import { MapPin, RefreshCw, Star } from '../../components/icons';
import { api } from '../../api/client';
import { TableSearch, useTableSearch } from '../../components/table-search';

// Admin Live Ops map — plots approved+active partners' current positions from
// GET /admin/delivery/live and refreshes every 10s (near-real-time). Positions
// update server-side on every delivery ping and when a partner goes online.

interface LivePartner {
  id: string;
  fullName: string;
  phoneE164: string;
  /** Truthful status: the toggle AND a recent heartbeat. */
  availabilityStatus: string;
  /** What the partner last chose — differs when their app stopped reporting. */
  declaredStatus?: string;
  /** Null until the partner has shared a position at least once. */
  lat: number | null;
  lng: number | null;
  lastUpdatedAt: string | null;
  ratingAvg: number;
  activeAssignmentId: string | null;
  activeStatus: string | null;
}

const MAPS_KEY = import.meta.env.VITE_GOOGLE_MAPS_JS_KEY as string | undefined;
const POLL_MS = 10_000;
const DEFAULT_CENTER = { lat: 20.5937, lng: 78.9629 }; // India

// Palette hexes (Google Maps markers can't take Tailwind classes):
// online = mehendi-600, on delivery = kumkum-500 (the accent, as en-route
// badges use), offline = ink-400.
const STATUS_COLOR: Record<string, string> = {
  ONLINE: '#3F6B2E',
  BUSY: '#D12A66',
  OFFLINE: '#9C9399',
};

/** Display-only: `ONLINE` → `Online`, `PICKED_UP` → `Picked up`. */
const sentence = (s: string) => {
  const lower = s.replace(/_/g, ' ').toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

function sinceLabel(iso: string | null): string {
  if (!iso) return 'unknown';
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const h = Math.floor(mins / 60);
  return `${h} hr${h === 1 ? '' : 's'} ago`;
}

export function LiveOpsMap() {
  const [partners, setPartners] = useState<LivePartner[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const { data } = await api.get<{ partners: LivePartner[] }>('/admin/delivery/live');
      setPartners(data.partners);
    } catch {
      /* keep last-known positions on a transient error */
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  const online = partners.filter((p) => p.availabilityStatus === 'ONLINE').length;
  const busy = partners.filter((p) => p.availabilityStatus === 'BUSY').length;
  // Partners with no position are listed but cannot be plotted. Counting them
  // separately stops the header disagreeing with the roster above it.
  const unlocated = partners.filter((p) => p.lat === null || p.lng === null).length;

  return (
    <div className="panel overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-surface-line px-5 py-4">
        <div className="min-w-0">
          <h3 className="text-[16px] font-semibold leading-snug text-ink-900">Live ops map</h3>
          <p className="mt-0.5 text-[13.5px] text-ink-500">
            {loading ? 'Loading partners…' : `${partners.length} partner${partners.length === 1 ? '' : 's'} · ${online} online · ${busy} on delivery${unlocated > 0 ? ` · ${unlocated} not sharing location` : ''}`}
          </p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          className="btn btn-secondary btn-sm"
        >
          <RefreshCw size={16}className={refreshing ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr]">
        <PartnerList partners={partners} selected={selected} onSelect={setSelected} />
        <div className="h-[420px]">
          {!MAPS_KEY ? (
            <MissingKey />
          ) : (
            <OpsMapView
              partners={partners}
              selected={selected}
              onSelect={setSelected}
            />
          )}
        </div>
      </div>
    </div>
  );
}

/** What the list row shows for a partner's state, e.g. "On delivery · Picked up". */
const stateLabel = (p: LivePartner) =>
  p.activeStatus ? `On delivery · ${sentence(p.activeStatus)}` : sentence(p.availabilityStatus);

function PartnerList({
  partners,
  selected,
  onSelect,
}: {
  partners: LivePartner[];
  selected: string | null;
  onSelect: (id: string) => void;
}) {
  // Client-side: the live feed already holds every approved partner. Matches
  // what each row shows — name, phone, state, location freshness, rating. The
  // map keeps plotting everyone; the search only narrows this list.
  const { query, setQuery, filtered } = useTableSearch(partners, (p) => [
    p.fullName || 'Partner',
    p.phoneE164,
    stateLabel(p),
    p.lat === null || p.lng === null ? 'no location shared' : sinceLabel(p.lastUpdatedAt),
    p.declaredStatus === 'ONLINE' && p.availabilityStatus !== 'ONLINE' ? 'Set to online but not reporting' : '',
    p.ratingAvg.toFixed(1),
  ]);

  return (
    <div className="flex max-h-[420px] flex-col border-b border-surface-line lg:border-b-0 lg:border-r">
      {partners.length > 0 && (
        <div className="shrink-0 border-b border-surface-line p-3">
          <TableSearch value={query} onChange={setQuery} placeholder="Search name, phone or status" />
        </div>
      )}
      <div className="min-h-0 flex-1 overflow-y-auto">
      {partners.length === 0 ? (
        <p className="p-5 text-[13.5px] text-ink-500">No approved partners yet. Partners appear here once approved and active.</p>
      ) : filtered.length === 0 ? (
        <div className="p-5 text-[13.5px] text-ink-500">
          <p>No partners match “{query.trim()}”.</p>
          <button type="button" onClick={() => setQuery('')} className="btn btn-secondary btn-sm mt-2.5">Clear search</button>
        </div>
      ) : (
        filtered.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => onSelect(p.id)}
            aria-pressed={selected === p.id}
            className={`flex w-full items-start gap-2.5 border-b border-surface-line px-4 py-3 text-left transition-colors ${
              selected === p.id ? 'bg-admin-soft' : 'hover:bg-surface-sunk'
            }`}
          >
            <span
              className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full"
              style={{ backgroundColor: STATUS_COLOR[p.availabilityStatus] ?? STATUS_COLOR.OFFLINE }}
            />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[14px] font-semibold text-ink-900">{p.fullName || 'Partner'}</span>
              <span className="block text-[12.5px] text-ink-500">
                {stateLabel(p)}
                {p.lat === null || p.lng === null
                  ? ' · no location shared'
                  : ` · ${sinceLabel(p.lastUpdatedAt)}`}
              </span>
              {p.declaredStatus === 'ONLINE' && p.availabilityStatus !== 'ONLINE' && (
                <span className="block text-[12.5px] font-semibold text-warning-ink">
                  Set to online but not reporting
                </span>
              )}
            </span>
            <span className="flex items-center gap-0.5 font-tabular text-[13px] text-ink-500">
              <Star size={12} className="fill-haldi-500 text-haldi-500" />
              {p.ratingAvg.toFixed(1)}
            </span>
          </button>
        ))
      )}
      </div>
    </div>
  );
}

function OpsMapView({
  partners,
  selected,
  onSelect,
}: {
  partners: LivePartner[];
  selected: string | null;
  onSelect: (id: string) => void;
}) {
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'admin-live-ops',
    googleMapsApiKey: MAPS_KEY!,
  });
  const mapRef = useRef<google.maps.Map | null>(null);
  const fittedRef = useRef(false);

  // Only partners who have shared a position can be plotted; the rest are still
  // listed on the left, labelled with why they are not here.
  const located = partners.filter(
    (p): p is LivePartner & { lat: number; lng: number } => p.lat !== null && p.lng !== null,
  );

  const onLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
  }, []);

  // Fit to all partners once we have positions and the map.
  useEffect(() => {
    if (!isLoaded || !mapRef.current || located.length === 0 || fittedRef.current) return;
    const bounds = new google.maps.LatLngBounds();
    located.forEach((p) => bounds.extend({ lat: p.lat, lng: p.lng }));
    mapRef.current.fitBounds(bounds, 48);
    if (located.length === 1) mapRef.current.setZoom(14);
    fittedRef.current = true;
  }, [isLoaded, located]);

  // Pan to a selected partner.
  useEffect(() => {
    if (!selected || !mapRef.current) return;
    const p = located.find((x) => x.id === selected);
    if (p) mapRef.current.panTo({ lat: p.lat, lng: p.lng });
  }, [selected, located]);

  if (loadError) {
    return <CenterNote text="Couldn’t load Google Maps — check the browser API key and its restrictions." />;
  }
  if (!isLoaded) {
    return <CenterNote text="Loading map…" />;
  }

  const sel = located.find((p) => p.id === selected) ?? null;

  return (
    <GoogleMap
      mapContainerStyle={{ width: '100%', height: '100%' }}
      center={DEFAULT_CENTER}
      zoom={5}
      onLoad={onLoad}
      options={{
        streetViewControl: false,
        mapTypeControl: false,
        fullscreenControl: false,
      }}
    >
      {located.map((p) => (
        <MarkerF
          key={p.id}
          position={{ lat: p.lat, lng: p.lng }}
          onClick={() => onSelect(p.id)}
          icon={{
            path: google.maps.SymbolPath.CIRCLE,
            scale: 8,
            fillColor: STATUS_COLOR[p.availabilityStatus] ?? STATUS_COLOR.OFFLINE,
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 2,
          }}
        />
      ))}
      {sel && (
        <InfoWindowF position={{ lat: sel.lat, lng: sel.lng }} onCloseClick={() => onSelect('')}>
          {/* Inline styles: the InfoWindow renders outside Tailwind's reach. Ink ramp hexes. */}
          <div style={{ minWidth: 160, fontFamily: 'Figtree, system-ui, sans-serif', color: '#1E1A1D' }}>
            <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>{sel.fullName || 'Partner'}</div>
            <div style={{ fontSize: 12.5, color: '#4A4248', fontFamily: '"JetBrains Mono", monospace' }}>{sel.phoneE164}</div>
            <div style={{ fontSize: 12.5, color: '#4A4248', marginTop: 4 }}>
              {sel.activeStatus ? `On delivery · ${sentence(sel.activeStatus)}` : sentence(sel.availabilityStatus)}
            </div>
            <div style={{ fontSize: 12, color: '#7A7177', marginTop: 2 }}>Updated {sinceLabel(sel.lastUpdatedAt)}</div>
          </div>
        </InfoWindowF>
      )}
    </GoogleMap>
  );
}

function CenterNote({ text }: { text: string }) {
  return (
    <div className="grid h-full place-items-center bg-surface-sunk px-6 text-center text-[13.5px] text-ink-500">{text}</div>
  );
}

function MissingKey() {
  return (
    <div className="grid h-full place-items-center bg-surface-sunk px-6">
      <div className="max-w-sm text-center">
        <MapPin className="mx-auto mb-2 text-ink-400" size={28} />
        <p className="text-[14.5px] font-semibold text-ink-900">Map key not configured</p>
        <p className="mt-1 text-[13.5px] text-ink-500">
          Set <code className="sku rounded-xs bg-surface-2 px-1">VITE_GOOGLE_MAPS_JS_KEY</code> (a browser key with the
          <b> Maps JavaScript API</b> enabled) and reload. The partner list on the left still works without it.
        </p>
      </div>
    </div>
  );
}
